<script>
  import { onMount } from 'svelte';
  import { getLoggedIn, getCartCount, getCurrentUrl } from '../../../../../js/stores';
  import { shouldRenderCta, shouldRenderSecondaryCta, filterNavItems } from '../../../../../js/navItemFilters';
  import { urlsAreEqual } from '../../../../../js/urlUtils';
  import { dispatchOffsetLeftOnResize } from '../../../../../js/dispatchOffsetLeftOnResize';

  import ButtonLink from '../../../ButtonLink/ButtonLink.svelte';
  import Link from './Link/Link.svelte';
  import CategoryFlattened from './CategoryFlattened/CategoryFlattened.svelte';
  import { setCtaSubnavButtonsVisibility } from '../../../../../js-v2/stores/desktop-cta-subnav-stores';

  const loggedInStore = getLoggedIn();
  const cartCountStore = getCartCount();
  const currentUrlStore = getCurrentUrl();

  export let data = {};
  export let isSticky = false;
  export let hiddenUntilScrolled = false;

  // headerHeight is bound to header height below
  // and used to size the shim element
  let headerHeight;

  // navWidth is bound to nav width below
  // and used to set the width of the header
  // (which it needs when it's position fixed)
  let navWidth;

  let subtitleOffsetLeft;

  let ctaWidth;
  let ctaHasFocus = false;
  $: ctaIsVisible = isSticky || ctaHasFocus || !data.onlyShowCtaWhenSticky;
  $: translateDistance = ctaIsVisible ? '0' : `${ctaWidth}px`;

  $: isDynamicVariation = data.enableDynamicVariations;

  // Determine whether or not to render CTA
  $: renderCta = shouldRenderCta({
    data,
    currentUrl: $currentUrlStore,
    loggedIn: $loggedInStore,
    cartCount: $cartCountStore,
  });

  // Filtered CTA item
  $: filteredCta = renderCta ? data.cta : undefined;

  // Sticky CTA is always a primary button
  $: ctaLinkStyle = filteredCta ? (isSticky ? 'primary' : filteredCta.linkStyle) : undefined;

  // Determine whether or not to render Secondary CTA
  $: renderSecondaryCta = shouldRenderSecondaryCta({
    data,
    loggedIn: $loggedInStore,
    cartCount: $cartCountStore,
  });

  // Filtered Secondary CTA item
  $: filteredSecondaryCta = renderSecondaryCta ? data.secondaryCta : undefined;

  // Filtered nav items
  $: filteredNavItems = filterNavItems({
    items: data.navItems,
    loggedIn: $loggedInStore,
    cartCount: $cartCountStore,
  });

  $: filteredNavItemsUrls = filteredNavItems.map((item) => item.url && item.url.split('?')[0]);

  $: reverseCtaButtons = filteredCta && filteredSecondaryCta && data.reverseDesktopCtaButtons;

  let useTransitions = false;
  let ctaSubnavDesktopHeader;

  $: setCtaSubnavButtonsVisibility({
    renderCta,
    renderSecondaryCta,
    ctaIsVisible,
    isSticky,
    hiddenUntilScrolled,
  });

  /**
   * Appends the dynamic variation query parameter to all links on the page if the URL matches links within the CTA
   * subnav.
   */
  function appendDynamicVariationSlug() {
    Object.values(document.links).forEach((link, i) => {
      const linkUrl = new URL(link.href);

      if (filteredNavItemsUrls.includes(linkUrl.pathname)) {
        // skip if the link already has a dynamic variation slug or a hash (e.g. modal links)
        if (linkUrl.searchParams.get('v') || link.href.includes('#')) {
          return;
        } else if (linkUrl.searchParams.size === 0) {
          document.links[i].href += `?v=${data.dynamicVariationSlug}`;
        } else {
          document.links[i].href += `&v=${data.dynamicVariationSlug}`;
        }
      }
    });
  }

  onMount(() => {
    // Resetting the position styling fixes the sticky nav not 'sticking' caused by deferred JS
    // Context: https://jira.sqprod.co/browse/WEBENG-4190
    if (ctaSubnavDesktopHeader) {
      ctaSubnavDesktopHeader.style.position = '';
    }

    if (data.enableDynamicVariations && data.dynamicVariationSlug) {
      appendDynamicVariationSlug();
    }

    // Hack to avoid transitioning CTA immediately on mount
    setTimeout(() => {
      useTransitions = true;
    }, 100);
  });

  function onCtaFocus() {
    ctaHasFocus = true;
  }
  function onCtaBlur() {
    ctaHasFocus = false;
  }
</script>

<div
  class="shim"
  aria-hidden="true"
  style:height={isSticky && !hiddenUntilScrolled ? `${headerHeight}px` : '0px'}
></div>

<nav class="CtaSubnavDesktopNav" bind:offsetWidth={navWidth}>
  <div
    class="header"
    class:isSticky
    class:useTransitions
    class:hiddenUntilScrolled
    class:isDynamicVariation
    style="width: {navWidth}px; --subtitle-offset-left: {subtitleOffsetLeft}px;"
    data-dynamic-variation={data.dynamicVariationSlug}
    bind:offsetHeight={headerHeight}
    bind:this={ctaSubnavDesktopHeader}
  >
    {#if data.enableDynamicVariations}
      <div class="left-slot" class:isDynamicVariation>
        {#if data.title && !isSticky}
          <span class="title" class:isDynamicVariation>{data.title}</span>
        {/if}
        {#if data.titleCategory}
          <CategoryFlattened
            id={data.titleCategory.id}
            title={data.titleCategory.title}
            navItems={data.titleCategory.navItems}
            spotlightConfig={data.titleCategory.spotlight}
            isVisible={isSticky}
          />
        {/if}
        {#if data.subtitle}
          <div class="vertical-divider"></div>
          <span
            class="subtitle"
            class:isDynamicVariation
            use:dispatchOffsetLeftOnResize
            on:offsetLeftOnResize={({ detail }) => {
              subtitleOffsetLeft = detail;
            }}
          >
            {data.subtitle}
          </span>
        {/if}
      </div>
      <div class="right-slot">
        {#if filteredCta || filteredSecondaryCta || filteredNavItems.length > 0}
          <ul
            class="nav-list"
            class:ctaIsVisible
            class:primaryCtaFirst={reverseCtaButtons}
            style="transform: translate({translateDistance});"
          >
            {#each filteredNavItems as item}
              <li class="link-item" class:hidden={item.hidden}>
                <Link
                  id={item.id}
                  title={item.title}
                  url={item.url}
                  isSelected={urlsAreEqual(item.url, $currentUrlStore)}
                  isDynamicVariation
                />
              </li>
            {/each}
            {#if filteredCta}
              <li
                class="cta-item"
                class:hidden={filteredCta.hidden || !isSticky}
                class:isVisible={ctaIsVisible}
                class:isDynamicVariation
                bind:offsetWidth={ctaWidth}
              >
                <ButtonLink
                  id={filteredCta.id}
                  title={filteredCta.title}
                  url={filteredCta.url}
                  linkStyle={ctaLinkStyle}
                  on:focus={onCtaFocus}
                  on:blur={onCtaBlur}
                />
              </li>
            {/if}
            {#if filteredSecondaryCta}
              <li
                class="cta-item isVisible"
                class:hidden={filteredSecondaryCta.hidden || !isSticky}
                class:isDynamicVariation
              >
                <ButtonLink
                  id={filteredSecondaryCta.id}
                  title={filteredSecondaryCta.title}
                  url={filteredSecondaryCta.url}
                  linkStyle={filteredSecondaryCta.linkStyle}
                />
              </li>
            {/if}
          </ul>
        {/if}
      </div>
    {:else}
      <div class="left-slot">
        {#if data.title}<span class="title">{data.title}</span>{/if}
      </div>
      <div class="right-slot">
        {#if filteredCta || filteredSecondaryCta || filteredNavItems.length > 0}
          <ul
            class="nav-list"
            class:ctaIsVisible
            class:primaryCtaFirst={reverseCtaButtons}
            style="transform: translate({translateDistance});"
          >
            {#each filteredNavItems as item}
              <li class="link-item" class:hidden={item.hidden}>
                <Link
                  id={item.id}
                  title={item.title}
                  url={item.url}
                  isSelected={urlsAreEqual(item.url, $currentUrlStore)}
                />
              </li>
            {/each}
            {#if filteredCta}
              <li
                class="cta-item"
                class:hidden={filteredCta.hidden}
                class:isVisible={ctaIsVisible}
                bind:offsetWidth={ctaWidth}
              >
                <ButtonLink
                  id={filteredCta.id}
                  title={filteredCta.title}
                  url={filteredCta.url}
                  linkStyle={ctaLinkStyle}
                  on:focus={onCtaFocus}
                  on:blur={onCtaBlur}
                />
              </li>
            {/if}
            {#if filteredSecondaryCta}
              <li class:hidden={filteredSecondaryCta.hidden} class="cta-item isVisible">
                <ButtonLink
                  id={filteredSecondaryCta.id}
                  title={filteredSecondaryCta.title}
                  url={filteredSecondaryCta.url}
                  linkStyle={filteredSecondaryCta.linkStyle}
                />
              </li>
            {/if}
          </ul>
        {/if}
      </div>
    {/if}
  </div>
</nav>

<style>@charset "UTF-8";
.left-slot.isDynamicVariation {
  display: flex;
  gap: 20px;
  align-items: center;
}

.shim {
  background-color: var(--color-background, #fff);
}

.header {
  border-width: 0;
  border-style: solid;
  color: var(--color-text, #1a1a1a);
  background-color: var(--color-background, #fff);
  border-color: var(--color-border, #d9d9d9);
  box-sizing: border-box;
  position: relative;
  min-width: 320px;
  border-bottom-width: 1px;
  overflow: hidden;
  z-index: 1;
  display: none;
  max-width: 100%;
  padding: 10px 15px 10px 25px;
  justify-content: space-between;
}
@media (min-width: 1280px) {
  .header {
    display: flex;
  }
}
.header.isDynamicVariation {
  padding: 0px 15px 0px 25px;
}

.isSticky {
  position: fixed;
  z-index: 2;
  top: 0;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.16);
}
.isSticky.isDynamicVariation {
  overflow: visible;
}
.isSticky.hiddenUntilScrolled {
  transition: transform 250ms cubic-bezier(0, 0, 0.2, 1);
  transform: translateY(0);
}

.title,
.subtitle {
  display: inline-block;
  padding: 10px 0;
  color: var(--color-text, #1a1a1a);
  font-family: "Square Sans Text VF", "Square Sans Text", "Helvetica", "Arial", "Hiragino Kaku Gothic Pro", "ヒラギノ角ゴ Pro W3", "メイリオ", meiryo, "ＭＳ Ｐゴシック", sans-serif;
  font-size: 16px;
  line-height: 28px;
  font-weight: var(--font-weight-medium, 500);
  letter-spacing: normal;
}
.title.isDynamicVariation,
.subtitle.isDynamicVariation {
  padding: 21px 0;
}

.nav-list {
  display: block;
  box-sizing: border-box;
  padding: 0;
  margin: 0;
  list-style-type: none;
  display: flex;
  align-items: center;
  height: 100%;
}
.nav-list > li {
  display: block;
  position: static;
  box-sizing: border-box;
  margin: 0;
  padding: 0;
}
.nav-list > li::before {
  display: none;
}
.nav-list > .cta-item {
  padding-right: 10px;
  padding-left: 10px;
  opacity: 0;
}
.nav-list > .cta-item.isVisible {
  opacity: 1;
}
.nav-list > .cta-item + .cta-item {
  padding-left: 0;
}

.useTransitions .nav-list {
  transition: transform 200ms cubic-bezier(0.4, 0, 0.2, 1);
}
.useTransitions .nav-list.ctaIsVisible {
  transition: transform 250ms cubic-bezier(0.4, 0, 0.2, 1);
}
.useTransitions .nav-list > .cta-item {
  transition: opacity 200ms cubic-bezier(0.4, 0, 0.2, 1);
}
.useTransitions .nav-list > .cta-item.isVisible {
  transition: opacity 250ms cubic-bezier(0.4, 0, 0.2, 1);
}

.hiddenUntilScrolled {
  position: fixed;
  z-index: 2;
  top: 0;
  transition: transform 200ms cubic-bezier(0.4, 0, 1, 1);
  transform: translateY(-100%);
}

.primaryCtaFirst :global(li.cta-item) {
  padding-left: 0;
  order: 1;
}
.primaryCtaFirst :global(li.cta-item) :global(+ li.cta-item) {
  padding-right: 10px;
  padding-left: 10px;
  order: 0;
}

.vertical-divider {
  width: 1px;
  height: 24px;
  background-color: #b3b3b3;
}</style>
